import type * as React from 'react';

/**
 * Combines multiple sets of event handlers together. For each unique event handler type, ensures that the resulting
 * callback for that handler calls all of the functions given for that key, in order of the functions passed in.
 *
 * For example, passing
 * { onClick: () => console.log('click 1') },
 * { onClick: () => console.log('click 2') }
 *
 * results in:
 * { onClick: () => fn }
 * where fn calls the onclicks in order, and prints:
 * 'click 1'
 * 'click 2'
 * @param args
 */
export default function combineHandlers(
    ...args: React.DOMAttributes<HTMLElement>[]
): React.DOMAttributes<HTMLElement> {
    // first figure out what unique handlers exist
    const handlerKeys: React.DOMAttributes<HTMLElement> = {};
    args.forEach(arg => {
        arg && Object.keys(arg).forEach(key => (handlerKeys[key] = undefined));
    });
    // then combine the handlers together
    Object.keys(handlerKeys).forEach(key => {
        const argsWithKey = args.filter(arg => arg && Object.keys(arg).some(k => k === key));
        const callback = (ev: React.UIEvent) => {
            argsWithKey.forEach(arg => {
                arg[key](ev);
            });
        };
        handlerKeys[key] = callback;
    });

    return handlerKeys;
}
